import axios from "axios";

const API_BASE_URL = "https://tpctrz.com:7002";

// ✅ Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15s timeout
  headers: {
    "Content-Type": "application/json",
    "OrgId": "reward",
  },
});

// ✅ Interceptor to attach token automatically
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      console.log("Token attached to request:", token.substring(0, 20) + "...");
    } else {
      console.log("No token found in storage");
    }
    console.log("API Request:", config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// ✅ Response interceptor for handling token expiration
api.interceptors.response.use(
  (response) => {
    console.log("API Response:", response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error("API Error:", error.response?.status, error.response?.data);
    
    // Handle token expiration
    if (error.response?.status === 401) {
      console.log("Token expired, clearing storage");
      sessionStorage.removeItem("token");
      localStorage.removeItem("token");
      // Optionally redirect to login
      window.location.href = "/";
    }
    
    return Promise.reject(error);
  }
);

// ---------- API FUNCTIONS ----------

// Helper function to extract response data
const extractResponseData = (apiResponse) => {
  return apiResponse?.data?.data[0]?.response || apiResponse?.data?.data || apiResponse?.data || {};
};

// Generic retry wrapper with exponential backoff
const requestWithRetry = async (fn, { retries = 2, baseDelayMs = 500 } = {}) => {
  let attempt = 0;
  let lastError;
  while (attempt <= retries) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const message = String(error?.message || '').toLowerCase();
      const netErr = (
        !status ||
        status === 408 ||
        status === 429 ||
        status === 500 ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        message.includes('network') ||
        message.includes('timeout') ||
        message.includes('forcibly closed') ||
        message.includes('wsasend')
      );
      if (!netErr || attempt === retries) break;
      const delay = baseDelayMs * Math.pow(2, attempt);
      await new Promise(r => setTimeout(r, delay));
      attempt += 1;
    }
  }
  throw lastError;
};

// Login
export const loginApi = (credentials) => 
  requestWithRetry(() => api.post("/auth/sso-register", credentials));

// Shop APIs
export const ShopDetails = async (data = {}) => {
  const response = await api.post("entities/filter/profile_update", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

export const fetchNearbyShops = async (data = {}) => {
  const response = await api.post("entities/filter/profile_update", data);
  return {
    
    ...response,
    extractedData: extractResponseData(response)
  };
};


export const PutBusinessProfile = async (id, data = {}) => {


  const response = await api.put(`entities/profile_update/${id}`, data);

  return {
    ...response,
    extractedData: extractResponseData(response),
  };
};


// User Profile APIs
export const AllProfile = async (data = {}) => {
  const response = await api.post("entities/filter/user", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

export const AllProfileUpdate = async (data = {}) => {
  const response = await api.post("entities/profile_update", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};


// Business API for the admin Approval
export const ApprovalBusiness = async (data = {}) => {
  const response = await api.post("entities/Approval", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};


export const getApprovalBusiness = async (data = {}) => {
  const response = await api.post("entities/filter/Approval", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};


export const PutApprovalBusiness = async (id, data = {}) => {
  if (!id) {
    throw new Error("Missing _id for Approval update");
  }

  const response = await api.put(`entities/Approval/${id}`, data);

  return {
    ...response,
    extractedData: extractResponseData(response),
  };
};



export const getProfileUpdate = async (data = {}) => {
  const response = await api.post("entities/filter/profile_update", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

// Helper function to process profile data with shop details and offers
export const processProfileData = (profileData) => {
  if (!profileData) return null;

  const processedProfile = {
    // User profile information
    profile_id: profileData.profile_id,
    email: profileData.email,
    firstName: profileData.firstName,
    lastName: profileData.lastName,
    phone: profileData.phone,
    address: profileData.address,
    dateOfBirth: profileData.dateOfBirth,
    registrationType: profileData.registrationType,
    status: profileData.status,
    created_on: profileData.created_on,
    created_by: profileData.created_by,
    
    // Process shop details if available
    shops: profileData.shopDetails ? profileData.shopDetails.map(shop => ({
      shopName: shop.shopName,
      description: shop.description,
      category: shop.category,
      rating: shop.rating || 0,
      mobile: shop.mobile,
      hours: shop.hours,
      specialDiscount: shop.specialDiscount,
      distance: shop[' distance'] || shop.distance, // Handle space in key
      location: {
        latitude: shop.location?.lat || 0,
        longitude: shop.location?.lng || 0
      },
      frontPhoto: shop.frontPhoto,
      insidePhoto: shop.insidePhoto,
      
      // Process offers
      offers: shop.offers ? shop.offers.map(offer => ({
        id: offer.id,
        title: offer.title,
        subtitle: offer.subtitle,
        code: offer.code,
        expiry: offer.expiry,
        discountValue: offer.title?.match(/(\d+)%/)?.[1] || 0, // Extract percentage from title
        discountType: 'percentage',
        expiryDate: calculateExpiryDate(offer.expiry),
        status: 'active'
      })) : []
    })) : [],
    
    // Summary data
    totalShops: profileData.shopDetails?.length || 0,
    totalOffers: profileData.shopDetails?.reduce((total, shop) => 
      total + (shop.offers?.length || 0), 0) || 0
  };

  return processedProfile;
};

// Helper function to calculate expiry date from relative time
const calculateExpiryDate = (expiryString) => {
  if (!expiryString) return new Date();
  
  const now = new Date();
  const match = expiryString.match(/(\d+)\s*(day|week|month)s?/i);
  
  if (match) {
    const value = parseInt(match[1]);
    const unit = match[2].toLowerCase();
    
    switch (unit) {
      case 'day':
        return new Date(now.getTime() + value * 24 * 60 * 60 * 1000);
      case 'week':
        return new Date(now.getTime() + value * 7 * 24 * 60 * 60 * 1000);
      case 'month':
        return new Date(now.getTime() + value * 30 * 24 * 60 * 60 * 1000);
      default:
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // Default 7 days
    }
  }
  
  return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // Default 7 days
};

export const getIdProfile = async (id = {}) => {
  const response = await api.post("entities/filter/profile_update", id);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

// Dataset API
export const fetchDataset = async (filterBody = {}) => {
  const response = await requestWithRetry(() => api.post("/dataset/data/customers_with_transactions", filterBody));
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

// Customer Shopping APIs
export const AllCustomerShopping = async (data = {}) => {
  const response = await requestWithRetry(() => api.post("entities/filter/customers", data));
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};


// Customer Business APIs
export const AllBusiness = async (data = {}) => {
  const response = await requestWithRetry(() => api.post("entities/filter/user", data));
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

// Profile Update Functions using AllProfileUpdate

// Update user profile (normal object)
export const updateUserProfile = async (profileData) => {
  const uid = sessionStorage.getItem('uid');
  if (!uid) {
    throw new Error('User ID not found in session storage');
  }

  const payload = {
    profile_id: uid,
    ...profileData
  };

  console.log('Updating user profile with payload:', payload);
  return await AllProfileUpdate(payload);
};

// Update shop details (array of objects)
export const updateShopDetails = async (shopDetailsArray) => {
  const uid = sessionStorage.getItem('uid');
  if (!uid) {
    throw new Error('User ID not found in session storage');
  }

  const payload = {
    profile_id: uid,
    shopDetails: Array.isArray(shopDetailsArray) ? shopDetailsArray : [shopDetailsArray]
  };

  console.log('Updating shop details with payload:', payload);
  return await AllProfileUpdate(payload);
};

// Update offers (array of objects)
export const updateOffers = async (offersArray) => {
  const uid = sessionStorage.getItem('uid');
  if (!uid) {
    throw new Error('User ID not found in session storage');
  }

  const payload = {
    profile_id: uid,
    offers: Array.isArray(offersArray) ? offersArray : [offersArray]
  };

  console.log('Updating offers with payload:', payload);
  return await AllProfileUpdate(payload);
};

// Update rewards (array of objects)
export const updateRewards = async (rewardsArray) => {
  const uid = sessionStorage.getItem('uid');
  if (!uid) {
    throw new Error('User ID not found in session storage');
  }

  const payload = {
    profile_id: uid,
    rewards: Array.isArray(rewardsArray) ? rewardsArray : [rewardsArray]
  };

  console.log('Updating rewards with payload:', payload);
  return await AllProfileUpdate(payload);
};

// Combined update function for multiple data types
export const updateProfileData = async (updateData) => {
  const uid = sessionStorage.getItem('uid');
  if (!uid) {
    throw new Error('User ID not found in session storage');
  }

  const payload = {
    profile_id: uid,
    ...updateData
  };

  console.log('Updating profile data with payload:', payload);
  return await AllProfileUpdate(payload);
};

// Create or update profile with embedded shopDetails (array of objects)
// profileObject is a plain object of profile fields (e.g., firstName, lastName, email, phone, etc.)
// shopDetailsArray can be a single object or an array of objects
export const createOrUpdateProfileWithShopDetails = async (profileObject = {}, shopDetailsArray = []) => {
  const uid = sessionStorage.getItem('uid');
  if (!uid) {
    throw new Error('User ID not found in session storage');
  }

  const payload = {
    profile_id: uid,
    ...profileObject,
    shopDetails: Array.isArray(shopDetailsArray) ? shopDetailsArray : [shopDetailsArray]
  };

  console.log('Creating/Updating profile with shopDetails payload:', payload);
  return await AllProfileUpdate(payload);
};

// Claim Request APIs (customer uploads bill and amount to request points from a business)
export const createClaimRequest = async (data = {}) => {
  const response = await api.post("entities/Claim", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

export const getClaimRequests = async (data = {}) => {
  const response = await api.post("entities/filter/Claim", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

export const updateClaimRequest = async (id, data = {}) => {
  if (!id) throw new Error("Missing _id for Claim update");
  const response = await api.put(`entities/Claim/${id}`, data);
  return {
    ...response,
    extractedData: extractResponseData(response),
  };
};

// Create a customer transaction (so existing lists like AllCustomerShopping pick it up)
export const createCustomerTransaction = async (data = {}) => {
  const response = await api.post("entities/customers", data);
  return {
    ...response,
    extractedData: extractResponseData(response)
  };
};

// Update a customer transaction (e.g., approve/reject pending redemption)
export const updateCustomerTransaction = async (id, data = {}) => {
  if (!id) throw new Error("Missing _id for customers update");
  const response = await api.put(`entities/customers/${id}`, data);
  return {
    ...response,
    extractedData: extractResponseData(response),
  };
};

export default api;
